/**
 * The running model + speech cost of the whole open conversation, shown as a
 * compact sum next to the panel's context chip (`AgentChatBox.js`). Numbers
 * only — `AgentChatBox` formats and translates the label itself via
 * `useFormatting()`/Tolgee, never here (this module has no React/i18n
 * context to do that correctly).
 */
export type SessionCostSummary = {
  totalUsd: number;
  modelUsd: number;
  /** Every answer's speech cost added so far (live, not-yet-booked figures included). */
  speechUsd: number;
  /** Answers whose model cost is known (contribute to `modelUsd`). */
  answered: number;
  /** Answers whose model cost the provider did not report. */
  unknown: number;
};

/**
 * Sums an open conversation's model cost from each assistant answer's own
 * cost (`null` when the provider reported none, counted separately as
 * `unknown` rather than as zero) plus, separately, every answer's speech
 * cost (omitted/`undefined`/`null` counts as no speech cost, not unknown —
 * unlike the model cost, most answers are simply never read aloud).
 */
export function summarizeSessionCost(
  answers: ReadonlyArray<{
    modelUsd: number | null;
    speechUsd?: number | null;
  }>
): SessionCostSummary {
  let modelUsd = 0;
  let speechUsd = 0;
  let answered = 0;
  let unknown = 0;
  for (const answer of answers) {
    if (answer.modelUsd === null) unknown++;
    else {
      modelUsd += answer.modelUsd;
      answered++;
    }
    if (answer.speechUsd) speechUsd += answer.speechUsd;
  }
  return {
    totalUsd: modelUsd + speechUsd,
    modelUsd,
    speechUsd,
    answered,
    unknown,
  };
}
